import React, { useState, useRef } from 'react';
import { User, Mail, Phone, Camera, KeyRound, Check, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { FormField } from '../components/Table';

const ROLE_LABELS = { partner: 'Partner', senior_staff: 'Senior Staff', associate: 'Associate', junior_staff: 'Junior Staff', super_admin: 'Partner', admin: 'Senior Staff', article: 'Junior Staff' };

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState({ name: user?.name || '', email: user?.email || '', mobile: user?.mobile || '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(user?.profile_photo || null);
  const fileRef = useRef();

  const handleProfileSave = async e => {
    e.preventDefault();
    if (!profile.name.trim()) { toast.error('Name is required'); return; }
    setProfileSaving(true);
    try {
      const r = await api.put('/auth/profile', profile);
      await refreshUser();
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to save');
    } finally { setProfileSaving(false); }
  };

  const handlePhotoChange = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Photo must be under 2 MB'); return; }
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const r = await api.post('/auth/upload-avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPhotoUrl(r.data.url);
      await refreshUser();
      toast.success('Photo updated');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Upload failed');
    } finally { setPhotoUploading(false); }
  };

  const initials = user?.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
        <p className="text-gray-500 text-sm mt-0.5">Manage your account details and password</p>
      </div>

      {/* Avatar + identity card */}
      <div className="card flex items-center gap-5">
        <div className="relative flex-shrink-0">
          <div className="w-20 h-20 rounded-full bg-[#1e3a5f] overflow-hidden flex items-center justify-center shadow-lg">
            {photoUrl ? (
              <img src={photoUrl} alt="Avatar" className="w-full h-full object-cover" />
            ) : (
              <span className="text-white text-2xl font-bold">{initials}</span>
            )}
          </div>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={photoUploading}
            className="absolute -bottom-1 -right-1 w-7 h-7 bg-[#f0a500] rounded-full flex items-center justify-center shadow hover:bg-[#d4921c] transition-colors"
            title="Change photo">
            <Camera size={13} className="text-white" />
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
        </div>
        <div>
          <div className="text-lg font-bold text-gray-900">{user?.name}</div>
          <div className="text-sm text-gray-500">{user?.designation || ROLE_LABELS[user?.role] || user?.role}</div>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-xs bg-[#1e3a5f]/10 text-[#1e3a5f] px-2.5 py-0.5 rounded-full font-medium">{ROLE_LABELS[user?.role] || user?.role}</span>
            <span className="text-xs text-gray-400 font-mono">{user?.employee_id}</span>
          </div>
        </div>
      </div>

      {/* Profile form */}
      <div className="card">
        <h2 className="font-semibold text-gray-900 mb-5 flex items-center gap-2"><User size={16} className="text-[#1e3a5f]" /> Personal Information</h2>
        <form onSubmit={handleProfileSave} className="space-y-4">
          <FormField label="Full Name *">
            <input value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} className="input-field" required />
          </FormField>
          <FormField label="Email Address">
            <div className="relative">
              <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="email" value={profile.email} onChange={e => setProfile(p => ({ ...p, email: e.target.value }))} className="input-field pl-9" />
            </div>
          </FormField>
          <FormField label="Mobile Number">
            <div className="relative">
              <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="tel" value={profile.mobile} onChange={e => setProfile(p => ({ ...p, mobile: e.target.value }))} className="input-field pl-9" placeholder="10-digit mobile" maxLength={10} />
            </div>
          </FormField>
          <FormField label="Role">
            <div className="input-field bg-gray-50 text-gray-500 cursor-not-allowed flex items-center gap-2">
              <Shield size={14} className="text-gray-400" />
              {ROLE_LABELS[user?.role] || user?.role}
              <span className="text-xs text-gray-400 ml-1">(cannot be changed here)</span>
            </div>
          </FormField>
          <button type="submit" disabled={profileSaving} className="btn-primary flex items-center gap-2">
            <Check size={15} /> {profileSaving ? 'Saving…' : 'Save Profile'}
          </button>
        </form>
      </div>

      {/* Change password */}
      <div className="card">
        <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><KeyRound size={16} className="text-[#1e3a5f]" /> Password</h2>
        <p className="text-sm text-gray-500 mb-4">Use a strong password with at least 8 characters including a number.</p>
        <button onClick={() => navigate('/change-password')} className="btn-secondary flex items-center gap-2">
          <KeyRound size={15} /> Change Password
        </button>
      </div>
    </div>
  );
}
